import { configure, defineRule } from 'vee-validate';
import i18n from '@/config/i18n';
import { parsePhoneNumberFromString } from 'libphonenumber-js/min';
import {
  email,
  integer,
  length,
  max,
  max_value,
  min,
  min_value,
  not_one_of,
  numeric,
  required,
} from '@vee-validate/rules';
import moment from 'moment';
import { TIME_FORMAT } from '@/filters/time';
import userService from '@/services/user.service';
import vendorsService from '@/services/vendors.service';
import locationsService from '@/services/locations.service';

defineRule('required', required);
defineRule('numeric', numeric);
defineRule('email', email);
defineRule('min', min_value);
defineRule('max', max_value);
defineRule('minLength', min);
defineRule('maxLength', max);
defineRule('excluded', not_one_of);
defineRule('integer', integer);
defineRule('length', length);

defineRule('greaterThan', (value, [arg]) => {
  return parseInt(value, 10) > arg;
});

defineRule('alphaNumeric', (value) => {
  if (!value || value.length === 0) {
    return true;
  }

  return /^[\p{L}\p{Nd}]+$/u.test(value);
});

defineRule('double', (value) => {
  const regex = /^\d+(\.\d{2})?$/;
  return regex.test(value);
});

defineRule('decimal', (value) => {
  if (!value || value.length === 0) {
    return true;
  }

  return /^\d+([.,]\d{1,2})?$/.test(value);
});

defineRule('otherFieldSet', (value, { otherFieldValue }) => {
  if (
    (value === null || value === undefined || value.toString() === '') &&
    otherFieldValue !== null &&
    otherFieldValue !== undefined &&
    otherFieldValue.toString() !== ''
  ) {
    return false;
  }

  return true;
});

defineRule('phone', (value) => {
  if (!value || value.length === 0) {
    return true;
  }

  const phoneNumberObject = parsePhoneNumberFromString(value);
  return phoneNumberObject !== undefined && phoneNumberObject.isValid();
});

defineRule('time', (value) => {
  if (!value || !value.length) {
    return true;
  }
  const time = moment(value, TIME_FORMAT);
  return time.format(TIME_FORMAT) === value; //
});

defineRule('afterTime', (value, [time]) => {
  if (!value || !value.length) {
    return true;
  }
  return moment(value, TIME_FORMAT).isAfter(moment(time, TIME_FORMAT));
});

let timeoutuniqueUserEmail = null;
defineRule('uniqueUserEmail', (value, [id]) => {
  clearTimeout(timeoutuniqueUserEmail);
  return new Promise((resolve) => {
    timeoutuniqueUserEmail = setTimeout(() => {
      userService
        .checkField({ id, email: value, app_id: 1 })
        .then((response) => {
          if (response.length > 0) {
            resolve(false);
          } else {
            resolve(true);
          }
        });
    }, 300);
  });
});

let timeoutuniqueUsername = null;
defineRule('uniqueUserUsername', (value, [id]) => {
  clearTimeout(timeoutuniqueUsername);
  return new Promise((resolve) => {
    timeoutuniqueUsername = setTimeout(() => {
      if (!value || value === '') {
        resolve(true);
        return;
      }
      userService.checkField({ id, username: value }).then((response) => {
        if (response.length > 0) {
          resolve(false);
        } else {
          resolve(true);
        }
      });
    }, 300);
  });
});

let timeoutuniqueVendorEmail = null;
defineRule('uniqueVendorEmail', (value, [id]) => {
  clearTimeout(timeoutuniqueVendorEmail);
  return new Promise((resolve) => {
    timeoutuniqueVendorEmail = setTimeout(() => {
      vendorsService
        .findAll({ id, email: value, app_id: 1 })
        .then((response) => {
          if (response.length > 0) {
            resolve(false);
          } else {
            resolve(true);
          }
        });
    }, 300);
  });
});

let timeoutUniqueVendorName = null;
defineRule('uniqueVendorName', (value, [id]) => {
  clearTimeout(timeoutUniqueVendorName);

  return new Promise((resolve) => {
    timeoutUniqueVendorName = setTimeout(() => {
      vendorsService
        .findAll({ id, name: value, app_id: 1 })
        .then((response) => {
          if (response.length > 0) {
            resolve(false);
          } else {
            resolve(true);
          }
        });
    }, 300);
  });
});

let timeoutUniqueVendorPublicName = null;
defineRule('uniqueVendorPublicName', (value, [id]) => {
  clearTimeout(timeoutUniqueVendorPublicName);

  return new Promise((resolve) => {
    timeoutUniqueVendorPublicName = setTimeout(() => {
      vendorsService
        .findAll({ id, public_name: value, app_id: 1 })
        .then((response) => {
          if (response.length > 0) {
            resolve(false);
          } else {
            resolve(true);
          }
        });
    }, 300);
  });
});

let timeoutUniqueVendorPhone = null;
defineRule('uniqueVendorPhone', (value, [id]) => {
  clearTimeout(timeoutUniqueVendorPhone);

  return new Promise((resolve) => {
    timeoutUniqueVendorPhone = setTimeout(() => {
      vendorsService
        .findAll({ id, phone: value, app_id: 1 })
        .then((response) => {
          if (response.length > 0) {
            resolve(false);
          } else {
            resolve(true);
          }
        });
    }, 300);
  });
});

const PIN_SEQUENCE_STEPS = [1, 9];

function isPinSequence(value) {
  return PIN_SEQUENCE_STEPS.some((step) =>
    [...value].every(
      (digit, index) =>
        index === 0 || Number(digit) === (Number(value[index - 1]) + step) % 10,
    ),
  );
}

function isPinRepetition(value) {
  for (let size = 1; size <= value.length / 2; size += 1) {
    if (
      value.length % size === 0 &&
      value.slice(0, size).repeat(value.length / size) === value
    ) {
      return true;
    }
  }

  return false;
}

defineRule('strongPin', (value) => {
  if (!value || value.length === 0) {
    return true;
  }

  return (
    new Set(value).size > 2 && !isPinSequence(value) && !isPinRepetition(value)
  );
});

defineRule('divider', (value, [divider]) => {
  return value % divider === 0;
});

let timeoutUniqueLocationName = null;
defineRule('uniqueLocationName', (value, [id, vendorId]) => {
  clearTimeout(timeoutUniqueLocationName);

  if (!value || value === '') {
    return true;
  }

  if (!vendorId) {
    return false;
  }

  return new Promise((resolve) => {
    timeoutUniqueLocationName = setTimeout(() => {
      locationsService
        .checkUniqueness({ id, name: value, vendor_id: vendorId })
        .then((response) => {
          if (response.length > 0) {
            resolve(false);
          } else {
            resolve(true);
          }
        })
        .catch(() => {
          resolve(false);
        });
    }, 300);
  });
});

let timeoutUniqueLocationShortName = null;
defineRule('uniqueLocationShortName', (value, [id, vendorId]) => {
  clearTimeout(timeoutUniqueLocationShortName);

  if (!value || value === '') {
    return true;
  }

  if (!vendorId) {
    return false;
  }

  return new Promise((resolve) => {
    timeoutUniqueLocationShortName = setTimeout(() => {
      locationsService
        .checkUniqueness({ id, short_name: value, vendor_id: vendorId })
        .then((response) => {
          if (response.length > 0) {
            resolve(false);
          } else {
            resolve(true);
          }
        })
        .catch(() => {
          resolve(false);
        });
    }, 300);
  });
});

configure({
  generateMessage: (context) => {
    if (context.rule.name === 'phone') {
      return i18n.global.t('validation.invalidPhone');
    }
    if (context.rule.name === 'required') {
      return i18n.global.t('validation.required');
    }
    if (context.rule.name === 'numeric') {
      return i18n.global.t('validation.numeric');
    }
    if (context.rule.name === 'email') {
      return i18n.global.t('validation.invalidEmail');
    }
    if (context.rule.name === 'alphaNumeric') {
      return i18n.global.t('validation.alphaNumeric');
    }
    if (context.rule.name === 'decimal') {
      return i18n.global.t('validation.decimal');
    }
    if (context.rule.name === 'min') {
      return i18n.global.t('validation.minimumValue', {
        min: context.rule.params[0],
      });
    }
    if (context.rule.name === 'max') {
      return i18n.global.t('validation.maximumValue', {
        max: context.rule.params[0],
      });
    }
    if (context.rule.name === 'minLength') {
      return i18n.global.t('validation.minimumLength', {
        min: context.rule.params[0],
      });
    }
    if (context.rule.name === 'maxLength') {
      return i18n.global.t('validation.maximumLength', {
        max: context.rule.params[0],
      });
    }
    if (context.rule.name === 'excluded') {
      return i18n.global.t('validation.excluded', {
        min: context.rule.params[0],
      });
    }
    if (context.rule.name === 'length') {
      return i18n.global.t('validation.length', {
        length: context.rule.params[0],
      });
    }
    if (context.rule.name === 'time') {
      return i18n.global.t('validation.invalidTime');
    }
    if (context.rule.name === 'afterTime') {
      return i18n.global.t('validation.afterTime', {
        time: context.rule.params[0],
      });
    }
    if (
      context.rule.name === 'uniqueUserEmail' ||
      context.rule.name === 'uniqueUserUsername' ||
      context.rule.name === 'uniqueVendorName' ||
      context.rule.name === 'uniqueVendorPublicName' ||
      context.rule.name === 'uniqueVendorPhone' ||
      context.rule.name === 'uniqueVendorEmail' ||
      context.rule.name === 'uniqueLocationName' ||
      context.rule.name === 'uniqueLocationShortName'
    ) {
      return i18n.global.t('validation.valueAlreadyRegistered', {
        value: context.value,
      });
    }
    if (context.rule.name === 'greaterThan') {
      return i18n.global.t('validation.greaterThan', {
        value: context.rule.params[0],
      });
    }
    if (context.rule.name === 'strongPin') {
      return i18n.global.t('validation.weakPin');
    }
    if (context.rule.name === 'divider') {
      return i18n.global.t('validation.divider', {
        value: context.rule.params[0],
      });
    }
    if (context.rule.name === 'otherFieldSet') {
      return i18n.global.t('validation.otherField', {
        value: context.rule.params.fieldHumanized,
      });
    }
  },
});

/*

extend('excluded', {
  ...excluded,
  message: (field, params) =>
    i18n.t('validation.excluded', { excluded: params._value_ })
});

extend('length', {
  ...length,
  message: (field, params) =>
    i18n.t('validation.length', { length: params.length })
});


extend('beforeOrSameTime', {
  validate(value, { time }) {
    return moment(value, TIME_FORMAT).isSameOrBefore(moment(time, TIME_FORMAT));
  },
  params: ['time'],
  message: (field, params, data) =>
    i18n.t('validation.sameOrBeforeTime', { time: params.time })
});

extend('afterOrSameTime', {
  validate(value, { time }) {
    return moment(value, TIME_FORMAT).isSameOrAfter(moment(time, TIME_FORMAT));
  },
  params: ['time'],
  message: (field, placehoder) => {
    return i18n.t('validation.sameOrAfterTime', { time: placehoder.time });
  }
});
*/
